// src/pages/TournamentDetails.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png';
import { auth } from '../firebase';
import { getDatabase, ref, get, onValue } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ArrowLeft, Wallet, Trophy, Users, Calendar,
  Lock, CheckCircle, AlertCircle, Copy, Check,
  Youtube, Instagram, Info, Award,
  Crosshair, TrendingUp, Crown, Medal, FileText,
  Shield, Activity, ChevronRight, Sparkles, Zap,
  Diamond, Target, MapPin, Server, Timer, Gift,
  Star, Flame, X, UserCheck, Building2, Loader2,
  User, Trophy as TrophyIcon, Layers,
} from 'lucide-react';

interface Tournament {
  id: string;
  title: string;
  date: string;
  time: string;
  image: string;
  prizePool: number;
  type: string;
  slots: number;
  registered: number;
  serverLogo: string;
  serverName: string;
  tier: string;
  organizerId: string;
  status: 'UPCOMING' | 'ONGOING' | 'COMPLETED';
  entryFee: number;
  mod: string;
  map: string;
  description: string;
  ytLink: string;
  instaUser: string;
  prizes: Record<string, string>;
  rounds?: Record<string, any>;
  roomId?: string;
  roomPassword?: string;
  canJoin?: boolean;
  multiOption?: boolean;
}

interface UserProfile {
  balance: number;
  name: string;
  userName: string;
  defaultTier: string;
  tierOverrides: Record<string, string>;
  matchPlayed: number;
  myTeamId?: string;
  BGMIIGN?: string;
  BGMIID?: string;
  FFMAXIGN?: string;
  FFMAXID?: string;
}

interface TeamData {
  teamId: string;
  teamName: string;
  game: string;
  password: string;
  createdAt?: number;
  status: 'complete' | 'incomplete';
  players: Record<string, {
    uid: string;
    name: string;
    userName: string;
    inGameName: string;
    inGameId: string;
    role: 'captain' | 'member';
    joinedAt: number;
  }>;
}

interface Team {
  teamId: string;
  teamName: string;
  password: string;
  slotNo: number | string;
  groupID: string;
  roomId?: string;
  roomPassword?: string;
  // ✅ ADD THESE
  wins?: number;
  matchPlayed?: number;
  posPoint?: number;
  killPoint?: number;
  players: Array<{
    ign: string;
    name: string;
    uid: string;
    winning?: number;
    position?: number;
  }>;
}

interface JoinTournamentResponse {
  success: boolean;
  message: string;
  teamId: string;
  teamName: string;
  players: Record<string, any>;
}

// ⭐ Updated: uses only join types the backend already understands
interface JoinTournamentData {
  game: string;
  matchId: string;
  joinType: 'solo' | 'verified' | 'create' | 'join';
  soloData?: {
    inGameName: string;
    inGameId: string;
  };
  // For empty-slot solo → creates a temp team
  teamData?: {
    teamName: string;
    teamPassword: string;
    inGameName: string;
    inGameId: string;
    slotNo?: number;
    position?: number;
  };
  // For occupied-slot solo → joins an existing temp team
  joinTeamData?: {
    teamId: string;
    teamPassword: string;
    inGameName: string;
    inGameId: string;
    slotNo?: number;
    position?: number;
  };
  // Verified full team
  verifiedTeamData?: {
    teamId: string;
    teamName: string;
    password: string;
    slotNo?: number;
    players: Record<string, any>;
  };
}

interface SlotSection {
  slotNo: number;
  teamId: string | null;
  teamName: string;
  teamPassword: string;
  filledPositions: number[];
  totalPositions: number;
  playersCount: number;
}

const TournamentDetails = () => {
  const navigate = useNavigate();
  const { game, matchId } = useParams<{ game: string; matchId: string }>();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [userTeam, setUserTeam] = useState<Team | null>(null);
  const [joinedTeams, setJoinedTeams] = useState<Team[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'teams' | 'prize' | 'result'>('details');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [showTeamDetailsModal, setShowTeamDetailsModal] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [hoveredTeam, setHoveredTeam] = useState<string | null>(null);
  const [userVerifiedTeam, setUserVerifiedTeam] = useState<TeamData | null>(null);
  const [loadingVerifiedTeam, setLoadingVerifiedTeam] = useState(false);
  const [showYoutubeNotLiveModal, setShowYoutubeNotLiveModal] = useState(false);

  const [inGameName, setInGameName] = useState('');
  const [inGameId, setInGameId] = useState('');

  const [joinMode, setJoinMode] = useState<'verified' | 'solo'>('verified');
  const [joinStep, setJoinStep] = useState<'form' | 'slot'>('form');
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);

  const [groupRoomInfo, setGroupRoomInfo] = useState<{ roomId: string; roomPassword: string } | null>(null);

  const isSolo = tournament?.type?.toLowerCase() === 'solo';
  const isDuo = tournament?.type?.toLowerCase() === 'duo';
  const isSquad = tournament?.type?.toLowerCase() === 'squad';
  const isHighTier =
    tournament?.tier === 'T2' ||
    tournament?.tier === 'T1' ||
    tournament?.tier === 'S' ||
    tournament?.tier === 'T2Q';

  const playersPerTeam = isSquad ? 4 : isDuo ? 2 : 1;
  const totalSections = tournament ? Math.ceil(tournament.slots / playersPerTeam) : 0;

  const hasResults = joinedTeams.some(team =>
    team.players.some(player => (player.winning || 0) > 0)
  );

  // ============================================
  // Load verified team
  // ============================================
  useEffect(() => {
    const loadUserVerifiedTeam = async () => {
      if (!userProfile?.myTeamId || !tournament) return;
      setLoadingVerifiedTeam(true);
      try {
        const db = getDatabase();
        const teamRef = ref(db, `allTeams/${game}/${userProfile.myTeamId}`);
        const snap = await get(teamRef);
        if (snap.exists()) {
          const d = snap.val();
          setUserVerifiedTeam({
            teamId: userProfile.myTeamId,
            teamName: d.teamName || '',
            game: d.game || game || '',
            password: d.password || '',
            status: d.status || 'incomplete',
            players: d.players || {},
          });
        } else {
          setUserVerifiedTeam(null);
        }
      } catch (e) {
        console.error('loadUserVerifiedTeam error:', e);
        setUserVerifiedTeam(null);
      } finally {
        setLoadingVerifiedTeam(false);
      }
    };
    loadUserVerifiedTeam();
  }, [userProfile?.myTeamId, tournament, game]);

  // ============================================
  // Main data loaders
  // ============================================
  useEffect(() => {
    if (!game || !matchId) return;
    const db = getDatabase();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate('/login');
      return;
    }

    let unsubTournament: (() => void) | undefined;
    let unsubUser: (() => void) | undefined;
    let unsubUserMatch: (() => void) | undefined;

    const loadData = async () => {
      setLoading(true);
      const tournamentRef = ref(db, `Tournaments/${game}/${matchId}`);
      unsubTournament = onValue(tournamentRef, (snapshot) => {
        if (snapshot.exists()) {
          const d = snapshot.val();
          setTournament({
            id: matchId,
            title: d.title || '',
            date: d.date || '',
            time: d.time || '',
            image: d.image || '',
            prizePool: Number(d.prizePool) || 0,
            type: d.type || 'SQUAD',
            slots: Number(d.slots) || 0,
            registered: Number(d.registered) || 0,
            serverLogo: d.serverLogo || '',
            serverName: d.serverName || '',
            tier: d.tier || 'T3',
            organizerId: d.organizerId || '',
            status: d.status || 'UPCOMING',
            entryFee: Number(d.entryFee) || 0,
            mod: d.mod || '',
            map: d.map || '',
            description: d.description || '',
            ytLink: d.ytLink || '',
            instaUser: d.instaUser || '',
            prizes: d.prizes || {},
            rounds: d.rounds || {},
            roomId: d.roomId,
            roomPassword: d.roomPassword,
            canJoin: d.canJoin !== undefined ? d.canJoin : true,
          multiOption:
              d.multiOption === undefined
                ? true
                : String(d.multiOption).toLowerCase() === 'true',
          });
        }
      });

      const userRef = ref(db, `Accounts/${currentUser.uid}`);
      unsubUser = onValue(userRef, (snapshot) => {
        if (snapshot.exists()) {
          const d = snapshot.val();
          setUserProfile({
            balance: Number(d.balance) || 0,
            name: d.name || '',
            userName: d.userName || '',
            defaultTier: d.defaultTier || 'T3',
            tierOverrides: d.tierOverrides || {},
            matchPlayed: d.matchPlayed || 0,
            myTeamId: d.myTeamId,
            BGMIIGN: d.BGMIIGN,
            BGMIID: d.BGMIID,
            FFMAXIGN: d.FFMAXIGN,
            FFMAXID: d.FFMAXID,
          });
        }
      });

      const userMatchRef = ref(db, `UserMatches/${currentUser.uid}/${matchId}`);
      unsubUserMatch = onValue(userMatchRef, async (snapshot) => {
        if (snapshot.exists()) {
          const val = snapshot.val();
          let teamId: string | undefined;
          if (typeof val === 'object' && val !== null) {
            teamId = val.teamId || val.id || val.teamID || (Object.values(val)[0] as string);
          } else {
            teamId = String(val);
          }
          if (!teamId || typeof teamId !== 'string') {
            setAlreadyJoined(false);
            setUserTeam(null);
            return;
          }
          try {
            const teamRef = ref(db, `Teams/${game}/${matchId}/teams/${teamId}`);
            const teamSnap = await get(teamRef);
            if (teamSnap.exists()) {
              const td = teamSnap.val();
              setUserTeam({
                teamId,
                teamName: td.teamName || '',
                password: td.password || '',
                slotNo: td.slotNo || 0,
                groupID: td.groupID || '',
                roomId: td.roomId,
                roomPassword: td.roomPassword,
                players: td.players
                  ? Object.entries(td.players).map(([k, p]: [string, any]) => ({
                      ign: p.ign || p.inGameName || '',
                      name: p.name || '',
                      uid: p.uid || '',
                      winning: p.winning || 0,
                      position: p.position || (isNaN(Number(k)) ? 0 : Number(k)),
                    }))
                  : [],
              });
              setAlreadyJoined(true);
            } else {
              setAlreadyJoined(false);
              setUserTeam(null);
            }
          } catch (e) {
            console.error('team fetch error:', e);
            setAlreadyJoined(false);
            setUserTeam(null);
          }
        } else {
          setAlreadyJoined(false);
          setUserTeam(null);
        }
        await loadAllTeams();
        setLoading(false);
      });

      await loadAllTeams();
    };

    loadData();

    return () => {
      unsubTournament?.();
      unsubUser?.();
      unsubUserMatch?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, matchId, navigate]);

  useEffect(() => {
    if (!showJoinModal) return;
    const profileIGN = game === 'BGMI' ? userProfile?.BGMIIGN : userProfile?.FFMAXIGN;
    const profileID = game === 'BGMI' ? userProfile?.BGMIID : userProfile?.FFMAXID;
    if (profileIGN && !inGameName) setInGameName(profileIGN);
    if (profileID && !inGameId) setInGameId(profileID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showJoinModal, game, userProfile]);

  useEffect(() => {
    if (!showJoinModal) {
      setJoinStep('form');
      setSelectedSection(null);
      setSelectedPosition(null);
      setError('');
    }
  }, [showJoinModal]);

    // If multiOption is disabled, force non-solo mode so the solo panel can't stick.
  useEffect(() => {
    console.log(tournament?.multiOption)
    if (!showJoinModal) return;
    if (!tournament?.multiOption && joinMode === 'solo') {
      setJoinMode('verified');
    }
    
  }, [showJoinModal, tournament?.multiOption, joinMode]);

  useEffect(() => {
    const fetchGroupRoomInfo = async () => {
      if (isSolo && tournament?.roomId && tournament?.roomPassword) {
        setGroupRoomInfo({
          roomId: tournament.roomId,
          roomPassword: tournament.roomPassword,
        });
        return;
      }
      if (!alreadyJoined || !userTeam || !tournament || !userTeam.groupID) {
        setGroupRoomInfo(null);
        return;
      }
      try {
        const db = getDatabase();
        const groupRef = ref(db, `Tournaments/${game}/${matchId}/groups/${userTeam.groupID}`);
        const groupSnap = await get(groupRef);
        if (groupSnap.exists()) {
          const gd = groupSnap.val();
          setGroupRoomInfo({
            roomId: gd.roomId || gd.roomID || '',
            roomPassword: gd.roomPassword || gd.roomPass || '',
          });
        } else {
          setGroupRoomInfo(null);
        }
      } catch (e) {
        console.error('fetchGroupRoomInfo error:', e);
        setGroupRoomInfo(null);
      }
    };
    fetchGroupRoomInfo();
  }, [alreadyJoined, userTeam, tournament, game, matchId, isSolo]);

  const loadAllTeams = async () => {
    if (!game || !matchId) return;
    const db = getDatabase();
    const teamsRef = ref(db, `Teams/${game}/${matchId}/teams`);
    const snapshot = await get(teamsRef);
    if (snapshot.exists()) {
      const teamsData = snapshot.val();
      const arr: Team[] = Object.entries(teamsData).map(([teamId, td]: [string, any]) => ({
        teamId,
        teamName: td.teamName || 'N/A',
        password: td.password || '',
        slotNo: td.slotNo || 0,
        groupID: td.groupID || 'DEFAULT',
        roomId: td.roomId,
        roomPassword: td.roomPassword,
        wins: Number(td.wins) || 0,
  matchPlayed: Number(td.matchPlayed) || 0,
  posPoint: Number(td.posPoint) || 0,
  killPoint: Number(td.killPoint) || 0,
        players: td.players
          ? Object.entries(td.players).map(([k, p]: [string, any]) => ({
              ign: p.ign || p.inGameName || p.name || 'Unknown',
              name: p.name || p.ign || 'Unknown',
              uid: p.uid || '',
              winning: p.winning || 0,
              position: p.position || (isNaN(Number(k)) ? 0 : Number(k)),
            }))
          : [],
      }));
      setJoinedTeams(arr);
    } else {
      setJoinedTeams([]);
    }
  };

  // Leaderboard — sorted by total points (KILL.PTS + FIN.PTS) descending
const leaderboard = useMemo(() => {
  return [...joinedTeams]
    .map((t) => {
      const killPts = Number(t.killPoint) || 0;   // shown as KILL.PTS
      const finPts  = Number(t.posPoint)  || 0;   // shown as FIN.PTS
      return {
        ...t,
        wins:         Number(t.wins)         || 0,
        matchPlayed:  Number(t.matchPlayed)  || 0,
        killPts,
        finPts,
        totalPts: killPts + finPts,
      };
    })
    .sort((a, b) => {
      if (b.totalPts !== a.totalPts) return b.totalPts - a.totalPts;
      if (b.wins !== a.wins) return b.wins - a.wins;       // tiebreak: more wins
      return (a.teamName || '').localeCompare(b.teamName || '');
    });
}, [joinedTeams]);
  // ============================================
  // Slot Sections
  // ============================================
  const slotSections: SlotSection[] = useMemo(() => {
    if (!tournament) return [];
    const out: SlotSection[] = [];
    for (let i = 1; i <= totalSections; i++) {
      const team = joinedTeams.find(t => Number(t.slotNo) === i);
      const filledPositions: number[] = [];
      if (team) {
        team.players.forEach((p, idx) => {
          const pos = p.position && p.position > 0 ? p.position : idx + 1;
          filledPositions.push(pos);
        });
      }
      out.push({
        slotNo: i,
        teamId: team?.teamId || null,
        teamName: team?.teamName || `Team ${i}`,
        teamPassword: team?.password || '111111',
        filledPositions,
        totalPositions: playersPerTeam,
        playersCount: team?.players.length || 0,
      });
    }
    return out;
  }, [joinedTeams, tournament, totalSections, playersPerTeam]);

  // ============================================
  // Helpers
  // ============================================
  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const parseDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return Date.now();
    const [day, month, year] = dateStr.split('/');
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours < 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    return new Date(Number(year), Number(month) - 1, Number(day), hours, minutes).getTime();
  };

  const getTimeRemaining = () => {
    if (!tournament) return '';
    const matchTime = parseDateTime(tournament.date, tournament.time);
    const diff = matchTime - Date.now();
    if (diff <= 0) return 'Started';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const getStatusConfig = () => {
    if (!tournament) return { text: '', color: '', bg: '', icon: null as any };
    switch (tournament.status) {
      case 'ONGOING':
        return { text: 'LIVE NOW', color: '#EF4444', bg: 'rgba(239,68,68,0.2)', icon: Flame };
      case 'COMPLETED':
        return { text: 'COMPLETED', color: '#6B7280', bg: 'rgba(107,114,128,0.2)', icon: Trophy };
      default:
        return { text: 'UPCOMING', color: '#10B981', bg: 'rgba(16,185,129,0.2)', icon: Zap };
    }
  };

  const formatINR = (amount: number) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const hasSufficientTier = () => {
    if (!userProfile || !tournament) return false;
    const order = ['T3', 'T2Q', 'T2', 'T1', 'S'];
    const u = order.indexOf(userProfile.defaultTier || 'T3');
    const t = order.indexOf(tournament.tier || 'T3');
    return u >= t;
  };

  const isTournamentFull = () => {
    if (!tournament) return false;
    return tournament.registered >= tournament.slots;
  };

  const getRequiredPlayersText = () => {
    if (isSquad) return '4 players (Squad)';
    if (isDuo) return '2 players (Duo)';
    return '1 player (Solo)';
  };

  const getTeamTypeLabel = () => {
    if (isSolo) return 'Solo Entry';
    if (isDuo) return 'Duo Entry';
    return 'Squad Entry';
  };

  const canVerifiedTeamJoin = () => {
    if (!userVerifiedTeam) return false;
    const count = Object.keys(userVerifiedTeam.players || {}).length;
    return count >= playersPerTeam;
  };

  const slotPercentage = tournament ? (tournament.registered / tournament.slots) * 100 : 0;
  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  // ============================================
  // Disabled reason helpers
  // ============================================
  const getSoloButtonDisabledReason = (): string | null => {
    if (!inGameName.trim()) return 'Enter your In-game Name (IGN)';
    if (!inGameId.trim()) return 'Enter your In-game ID';
    if (!userProfile) return 'Loading your profile...';
    if ((userProfile.balance || 0) < (tournament?.entryFee || 0)) {
      return `Insufficient balance. Need ${formatINR(tournament!.entryFee)} — you have ${formatINR(
        userProfile.balance || 0
      )}. Please add funds.`;
    }
    return null;
  };

  const getVerifiedButtonDisabledReason = (): string | null => {
    if (!userVerifiedTeam) return 'No verified team found';
    if (!canVerifiedTeamJoin()) return `Your team needs ${getRequiredPlayersText()}`;
    const total =
      (tournament?.entryFee || 0) * Object.keys(userVerifiedTeam.players || {}).length;
    if ((userProfile?.balance || 0) < total) {
      return `Insufficient balance. Need ${formatINR(total)} for the whole team.`;
    }
    return null;
  };

  // ============================================
  // Join Flow Handlers
  // ============================================
  const handleContinueToSlot = (modeOverride?: 'verified' | 'solo') => {
    const mode = modeOverride ?? joinMode;
    setError('');
    if (mode === 'solo') {
      if (!inGameName.trim() || !inGameId.trim()) {
        setError('Please enter your In-game Name and ID');
        return;
      }
      if (!userProfile) {
        setError('Profile still loading. Please wait a moment.');
        return;
      }
      if ((userProfile.balance || 0) < (tournament?.entryFee || 0)) {
        setError('Insufficient balance. Please add funds.');
        return;
      }
    } else if (mode === 'verified') {
      if (!userVerifiedTeam) {
        setError('No verified team found');
        return;
      }
      if (!canVerifiedTeamJoin()) {
        setError(`Your team needs ${getRequiredPlayersText()}`);
        return;
      }
      const total = (tournament?.entryFee || 0) * Object.keys(userVerifiedTeam.players).length;
      if ((userProfile?.balance || 0) < total) {
        setError(`Insufficient balance. Need ${formatINR(total)}`);
        return;
      }
    }
    setJoinStep('slot');
  };

  // ⭐ FIXED: Uses 'create' or 'join' join types accepted by the backend
  const handleConfirmSoloSlotJoin = async () => {
    if (!tournament || !userProfile) return;
    if (selectedSection === null || selectedPosition === null) {
      setError('Please select a slot');
      return;
    }
    const sec = slotSections.find(s => s.slotNo === selectedSection);
    if (!sec) {
      setError('Invalid slot');
      return;
    }
    if (sec.filledPositions.includes(selectedPosition)) {
      setError('That position is already taken. Please pick another.');
      return;
    }

    const isEmpty = sec.filledPositions.length === 0;

    setJoinLoading(true);
    setError('');
    try {
      const functions = getFunctions();
      const joinFn = httpsCallable<JoinTournamentData, JoinTournamentResponse>(
        functions,
        'joinTournament'
      );

      let payload: JoinTournamentData;

      if (isEmpty) {
        // 🟢 Empty slot → create a temp team (name "Team N", password "111111")
        payload = {
          game: game!,
          matchId: matchId!,
          joinType: 'create',
          teamData: {
            teamName: `Team ${selectedSection}`,
            teamPassword: '111111',
            inGameName,
            inGameId,
            slotNo: selectedSection,
            position: selectedPosition,
          },
        };
      } else {
        // 🟡 Partially filled slot → join the existing temp team
        if (!sec.teamId) {
          setError('Team not found for this slot. Please refresh.');
          setJoinLoading(false);
          return;
        }
        payload = {
          game: game!,
          matchId: matchId!,
          joinType: 'join',
          joinTeamData: {
            teamId: sec.teamId,
            teamPassword: sec.teamPassword || '111111',
            inGameName,
            inGameId,
            slotNo: selectedSection,
            position: selectedPosition,
          },
        };
      }

      const res = await joinFn(payload);
      const data = res.data;

      if (data.success) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const userRef = ref(getDatabase(), `Accounts/${currentUser.uid}`);
          const snap = await get(userRef);
          if (snap.exists()) setUserProfile(snap.val());
        }
        await loadAllTeams();
        setAlreadyJoined(true);
        setShowJoinModal(false);
        alert(
          isEmpty
            ? `✅ Created Team ${selectedSection} and joined as player ${selectedPosition}!`
            : `✅ Joined Team ${selectedSection} as player ${selectedPosition}!`
        );
      } else {
        setError(data.message || 'Failed to join');
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Something went wrong');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleConfirmVerifiedSlotJoin = async () => {
    if (!tournament || !userProfile || !userVerifiedTeam) return;
    if (selectedSection === null) {
      setError('Please select a slot');
      return;
    }
    const sec = slotSections.find(s => s.slotNo === selectedSection);
    if (!sec) {
      setError('Invalid slot');
      return;
    }
    if (sec.filledPositions.length > 0) {
      setError('That slot is already occupied. Please choose an empty slot.');
      return;
    }

    setJoinLoading(true);
    setError('');
    try {
      const players: Record<string, any> = {};
      let idx = 1;
      for (const [, p] of Object.entries(userVerifiedTeam.players)) {
        const pd = p as any;
        players[`player${idx}`] = {
          uid: pd.uid || '',
          name: pd.name || pd.userName || 'Unknown',
          ign: pd.inGameName || pd.ign || 'Unknown',
          id: pd.inGameId || pd.id || 'Unknown',
          role: pd.role || 'member',
          position: idx,
          joinedAt: Date.now(),
        };
        idx++;
      }

      const functions = getFunctions();
      const joinFn = httpsCallable<JoinTournamentData, JoinTournamentResponse>(
        functions,
        'joinTournament'
      );
      const res = await joinFn({
        game: game!,
        matchId: matchId!,
        joinType: 'verified',
        verifiedTeamData: {
          teamId: userVerifiedTeam.teamId,
          teamName: userVerifiedTeam.teamName,
          password: userVerifiedTeam.password || '',
          slotNo: selectedSection,
          players,
        },
      });

      const data = res.data;
      if (data.success) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const userRef = ref(getDatabase(), `Accounts/${currentUser.uid}`);
          const snap = await get(userRef);
          if (snap.exists()) setUserProfile(snap.val());
        }
        await loadAllTeams();
        setAlreadyJoined(true);
        setShowJoinModal(false);
        alert(`✅ ${userVerifiedTeam.teamName} allocated to Slot ${selectedSection}!`);
      } else {
        setError(data.message || 'Failed to join');
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Something went wrong');
    } finally {
      setJoinLoading(false);
    }
  };

  // ============================================
  // Slot grid renderer
  // ============================================
  const renderSlotGrid = () => {
    if (!tournament) return null;
    const isVerifiedPick = joinMode === 'verified';

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setJoinStep('form');
              setSelectedSection(null);
              setSelectedPosition(null);
              setError('');
            }}
            className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <p className="text-white font-rajdhani font-bold">
            {isVerifiedPick ? 'Pick an empty slot' : 'Pick your slot'}
          </p>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
          <p className="text-blue-400 text-xs flex items-center gap-1">
            <Info className="w-3 h-3" />
            {isVerifiedPick
              ? `Select one empty slot. Your whole team (${playersPerTeam} players) will be placed there.`
              : 'Select one position. If the slot is empty, a new team will be created. If partially filled, you will join the existing team.'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto pr-1">
          {slotSections.map(sec => {
            const isFull = sec.filledPositions.length >= sec.totalPositions;
            const isEmpty = sec.filledPositions.length === 0;

            if (isVerifiedPick) {
              const selected = selectedSection === sec.slotNo;
              return (
                <button
                  key={sec.slotNo}
                  disabled={!isEmpty}
                  onClick={() => setSelectedSection(sec.slotNo)}
                  className={`rounded-xl p-3 border-2 transition-all text-left ${
                    selected
                      ? 'border-purple-500 bg-purple-500/20 shadow-lg shadow-purple-500/25'
                      : !isEmpty
                      ? 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed'
                      : 'border-white/20 bg-white/5 hover:border-purple-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-rajdhani font-bold text-sm">
                      Slot {sec.slotNo}
                    </p>
                    {selected ? (
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                    ) : !isEmpty ? (
                      <Lock className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Target className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: sec.totalPositions }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-4 h-4 rounded-full ${
                          sec.filledPositions.includes(i + 1)
                            ? 'bg-green-500'
                            : 'bg-white/20'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">
                    {sec.filledPositions.length}/{sec.totalPositions} filled
                  </p>
                </button>
              );
            }

            return (
              <div
                key={sec.slotNo}
                className={`rounded-xl p-3 border-2 ${
                  selectedSection === sec.slotNo
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-white/15 bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white font-rajdhani font-bold text-sm">
                    Slot {sec.slotNo}
                  </p>
                  {isFull && <Lock className="w-3 h-3 text-gray-500" />}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: sec.totalPositions }).map((_, i) => {
                    const pos = i + 1;
                    const filled = sec.filledPositions.includes(pos);
                    const selected =
                      selectedSection === sec.slotNo && selectedPosition === pos;
                    return (
                      <button
                        key={pos}
                        disabled={filled}
                        onClick={() => {
                          setSelectedSection(sec.slotNo);
                          setSelectedPosition(pos);
                        }}
                        className={`aspect-square rounded-lg flex items-center justify-center text-xs font-rajdhani font-bold transition-all ${
                          selected
                            ? 'bg-gradient-to-br from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/25 scale-105'
                            : filled
                            ? 'bg-green-500/20 text-green-400 cursor-not-allowed'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        {filled ? <CheckCircle className="w-4 h-4" /> : pos}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {selectedSection !== null && (
          <div className="bg-white/5 rounded-xl p-3 border border-purple-500/30">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Selected Slot</span>
              <span className="text-white font-rajdhani font-bold">
                Slot {selectedSection}
                {!isVerifiedPick && selectedPosition !== null
                  ? ` · Position ${selectedPosition}`
                  : ''}
              </span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-400">Entry Fee</span>
              <span className="text-yellow-500 font-rajdhani font-bold">
                {isVerifiedPick
                  ? formatINR(tournament.entryFee * playersPerTeam)
                  : formatINR(tournament.entryFee)}
              </span>
            </div>
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={isVerifiedPick ? handleConfirmVerifiedSlotJoin : handleConfirmSoloSlotJoin}
          disabled={
            joinLoading || selectedSection === null || (!isVerifiedPick && selectedPosition === null)
          }
          className="w-full bg-gradient-to-r from-purple-500 to-blue-500 py-3 rounded-xl text-white font-rajdhani font-bold text-base hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50"
        >
          {joinLoading ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : (
            `Confirm Join - ${
              isVerifiedPick
                ? formatINR(tournament.entryFee * playersPerTeam)
                : formatINR(tournament.entryFee)
            }`
          )}
        </motion.button>
      </div>
    );
  };

  // ============================================
  // Loading / Not Found
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
        <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="relative flex flex-col items-center">
          <div className="relative w-28 h-28">
            <div className="absolute inset-0 rounded-full">
              <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="50%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#EC4899" />
                  </linearGradient>
                </defs>
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  fill="none"
                  stroke="url(#ringGradient)"
                  strokeWidth="3"
                  strokeDasharray="200"
                  strokeDashoffset="50"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <img src={logo} alt="ArenaX" className="w-20 h-20 object-contain" />
            </div>
          </div>
          <p className="mt-5 text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
            LOADING ARENA...
          </p>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] to-[#1a1a2e] flex items-center justify-center px-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">Tournament Not Found</h2>
          <p className="text-gray-400 mb-6">
            The tournament you're looking for doesn't exist or has been removed
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(`/game/${game}`)}
            className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white font-rajdhani font-bold"
          >
            Back to Tournaments
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* Header */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#0f0f1a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="px-4 md:px-8 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/game/${game}`)}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </motion.button>
            <div className="text-center">
              <h1 className="text-lg font-rajdhani font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent tracking-wider">
                TOURNAMENT
              </h1>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <div
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: statusConfig.color }}
                />
                <span className="text-xs font-rajdhani font-bold" style={{ color: statusConfig.color }}>
                  {statusConfig.text}
                </span>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard/wallet')}
              className="flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-3 py-2 rounded-xl border border-yellow-500/30"
            >
              <Wallet className="w-5 h-5 text-yellow-500" />
              <span className="text-yellow-500 font-rajdhani font-bold text-sm">
                {formatINR(userProfile?.balance || 0)}
              </span>
            </motion.button>
          </div>
        </div>
      </div>

      <div className="pt-20 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-6"
        >
          <img
            src={
              tournament.image ||
              'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1200&h=400&fit=crop'
            }
            alt={tournament.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
          {tournament.ytLink && tournament.ytLink !== 'NOT UPDATED' && (
            <div className="absolute top-3 right-3 z-10">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50">
                <div className="w-2 h-2 rounded-full bg-white animate-ping" />
                <span className="text-white font-rajdhani font-bold text-xs tracking-wider">
                  🔴 LIVE
                </span>
              </div>
            </div>
          )}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex flex-wrap gap-2 mb-2">
              <div
                className="px-3 py-1 rounded-lg text-xs font-rajdhani font-bold backdrop-blur-sm border"
                style={{
                  backgroundColor: statusConfig.bg,
                  borderColor: statusConfig.color,
                  color: statusConfig.color,
                }}
              >
                {StatusIcon && <StatusIcon className="w-3 h-3 inline mr-1" />}
                {statusConfig.text}
              </div>
              <div className="px-3 py-1 rounded-lg text-xs font-rajdhani font-bold bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                TIER {tournament.tier}
              </div>
              <div className="px-3 py-1 rounded-lg text-xs font-rajdhani font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white">
                {tournament.type} • {getTeamTypeLabel()}
              </div>
            </div>
            <h2 className="text-white font-rajdhani font-bold text-2xl md:text-3xl">
              {tournament.title}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <Server className="w-4 h-4 text-gray-400" />
              <p className="text-gray-400 text-sm">{tournament.serverName}</p>
            </div>
          </div>
        </motion.div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-2xl p-5 border border-yellow-500/30">
            <Gift className="w-5 h-5 text-yellow-500 mb-2" />
            <p className="text-gray-400 text-xs mb-1">Entry Fee</p>
            <p className="text-3xl font-rajdhani font-bold text-yellow-500">
              {tournament.entryFee > 0 ? formatINR(tournament.entryFee) : 'FREE'}
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-2xl p-5 border border-purple-500/30">
            <Trophy className="w-5 h-5 text-purple-400 mb-2" />
            <p className="text-gray-400 text-xs mb-1">Prize Pool</p>
            <p className="text-3xl font-rajdhani font-bold text-purple-400">
              {formatINR(tournament.prizePool)}
            </p>
          </div>
        </div>

        {/* Slots + Date/Time */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400 text-sm font-rajdhani">Slots Filled</span>
              <span className="text-white font-rajdhani font-bold text-lg">
                {tournament.registered}/{tournament.slots}
              </span>
            </div>
            <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${slotPercentage}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
              />
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">
                {tournament.slots - tournament.registered} spots left
              </span>
              <span className="text-purple-400">{Math.round(slotPercentage)}% filled</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-4 border border-purple-500/30">
              <Calendar className="w-5 h-5 text-purple-400 mb-2" />
              <p className="text-gray-400 text-xs mb-1">Date</p>
              <p className="text-white font-rajdhani font-bold text-lg">{tournament.date}</p>
            </div>
            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-4 border border-purple-500/30">
              <Timer className="w-5 h-5 text-purple-400 mb-2" />
              <p className="text-gray-400 text-xs mb-1">Time</p>
              <p className="text-white font-rajdhani font-bold text-lg">{tournament.time}</p>
              {tournament.status === 'UPCOMING' && (
                <p className="text-yellow-500 text-xs mt-1">Starts in {getTimeRemaining()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Join Status */}
        {alreadyJoined && userTeam ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500 rounded-2xl p-4 mb-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-rajdhani font-bold">
                  You have joined as {userTeam.teamName}
                </span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowTeamDetailsModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white text-sm font-rajdhani font-bold"
              >
                View My Team
              </motion.button>
            </div>
          </motion.div>
        ) : tournament.status === 'UPCOMING' ? (
          (() => {
            const canJoinFromDB = tournament.canJoin !== false;
            const hasTier = hasSufficientTier();
            const userTier = userProfile?.defaultTier || 'T3';
            const full = isTournamentFull();

            if (!canJoinFromDB) {
              return (
                <div className="mb-6 bg-yellow-500/20 border-2 border-yellow-500 rounded-2xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Lock className="w-6 h-6 text-yellow-400" />
                    <span className="text-yellow-400 font-rajdhani font-bold text-lg">
                      REGISTRATION NOT STARTED
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm">
                    Registration has not started yet or has been closed by admin.
                  </p>
                </div>
              );
            }

            if (!hasTier) {
              return (
                <div className="mb-6 bg-red-500/20 border-2 border-red-500 rounded-2xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Lock className="w-6 h-6 text-red-400" />
                    <span className="text-red-400 font-rajdhani font-bold text-lg">TIER LOCKED</span>
                  </div>
                  <p className="text-gray-300 text-sm">
                    Requires <span className="text-yellow-400 font-bold">{tournament.tier}</span>{' '}
                    tier. Your tier:{' '}
                    <span className="text-blue-400 font-bold">{userTier}</span>.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/dashboard/profile')}
                    className="mt-3 px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white font-rajdhani font-bold text-sm"
                  >
                    View Profile & Tiers
                  </motion.button>
                </div>
              );
            }

            if (full) {
              return (
                <div className="mb-6 bg-red-500/20 border-2 border-red-500 rounded-2xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <X className="w-6 h-6 text-red-400" />
                    <span className="text-red-400 font-rajdhani font-bold text-lg">
                      REGISTRATION CLOSED
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm">
                    All {tournament.slots} slots have been filled.
                  </p>
                </div>
              );
            }

            return (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (isSquad) setJoinMode('verified');
                  else if (tournament.multiOption) setJoinMode('solo');
                  else setJoinMode('verified'); // placeholder; UI hides the panels
                  setJoinStep('form');
                  setShowJoinModal(true);
                }}
                className="relative group overflow-hidden w-full py-4 rounded-2xl text-white font-rajdhani font-bold text-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:shadow-lg hover:shadow-purple-500/25 transition-all mb-6"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                <span className="relative z-10">
                  Join Tournament{' '}
                  {tournament.entryFee > 0 ? `- ${formatINR(tournament.entryFee)}` : '- FREE'}
                </span>
              </motion.button>
            );
          })()
        ) : tournament.status === 'ONGOING' ? (
          <button className="w-full py-4 rounded-2xl text-white font-rajdhani font-bold text-lg bg-gradient-to-r from-red-500 to-red-600 opacity-80 cursor-not-allowed mb-6">
            Tournament In Progress
          </button>
        ) : (
          <button className="w-full py-4 rounded-2xl text-gray-400 font-rajdhani font-bold text-lg bg-white/10 cursor-not-allowed mb-6">
            Tournament Completed
          </button>
        )}

        {/* My Team / Room Info */}
        {alreadyJoined && userTeam && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl p-5 mb-6 border-2 border-purple-500/50"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-rajdhani font-bold text-xl">Your Team Details</h3>
                <p className="text-gray-400 text-xs">Team information for this tournament</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-3 border border-green-500/30 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-green-400" />
                <p className="text-green-400 text-sm font-rajdhani font-bold">Room Information</p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 rounded-lg p-2 text-center border border-blue-500/30">
                  <p className="text-gray-400 text-[10px] uppercase tracking-wider font-rajdhani">
                    Slot
                  </p>
                  <p className="text-white font-rajdhani font-bold text-lg">
                    {userTeam.slotNo || 'NA'}
                  </p>
                </div>
                <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 rounded-lg p-2 text-center border border-purple-500/30">
                  <p className="text-gray-400 text-[10px] uppercase tracking-wider font-rajdhani">
                    Group
                  </p>
                  <p className="text-white font-rajdhani font-bold text-lg">
                    {userTeam.groupID || 'NA'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Room ID</p>
                  <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                    <code className="text-green-400 font-mono text-sm break-all">
                      {groupRoomInfo?.roomId || 'NOT UPDATED'}
                    </code>
                    {groupRoomInfo?.roomId && (
                      <button
                        onClick={() => copyToClipboard(groupRoomInfo.roomId)}
                        className="text-green-400 hover:text-green-300 ml-2"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Password</p>
                  <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                    <code className="text-green-400 font-mono text-sm break-all">
                      {groupRoomInfo?.roomPassword || 'NOT UPDATED'}
                    </code>
                    {groupRoomInfo?.roomPassword && (
                      <button
                        onClick={() => copyToClipboard(groupRoomInfo.roomPassword)}
                        className="text-green-400 hover:text-green-300 ml-2"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-yellow-500 text-xs mt-3 flex items-center gap-1 bg-yellow-500/10 p-2 rounded-lg border border-yellow-500/20">
                <AlertCircle className="w-3 h-3" />
                {!groupRoomInfo?.roomId
                  ? 'Room details will be updated by admin before the tournament starts.'
                  : 'Join the room before tournament start time.'}
              </p>
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-500/20 border border-red-500 rounded-2xl p-3"
          >
            <p className="text-red-400 text-sm text-center">{error}</p>
          </motion.div>
        )}

        {/* Tab Bar */}
        <div className="bg-white/5 rounded-xl p-1 backdrop-blur-sm border border-white/10 mb-6">
          <div className="flex gap-1">
            {[
              { id: 'details', label: 'Details', icon: Info },
              { id: 'teams', label: 'Teams', icon: Users, count: joinedTeams.length },
              { id: 'prize', label: 'Prize', icon: Trophy },
              { id: 'result', label: 'Result', icon: Award },
            ].map(tab => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-rajdhani font-bold text-sm transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count && tab.count > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'details' && (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
                <h3 className="text-white font-rajdhani font-bold text-lg mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-400" />
                  Match Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-400">Game Mode</span>
                    <span className="text-white font-rajdhani font-bold">{tournament.mod}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-400">Match Type</span>
                    <span className="text-white font-rajdhani font-bold">{tournament.type}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-400">Map</span>
                    <span className="text-white font-rajdhani font-bold">
                      {tournament.map || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-400">Server</span>
                    <span className="text-white font-rajdhani font-bold">
                      {tournament.serverName}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-400">Team Size</span>
                    <span className="text-white font-rajdhani font-bold">
                      {isSolo ? '1 Player' : isDuo ? '2 Players' : '4 Players'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/10">
                    <span className="text-gray-400">Tournament Tier</span>
                    <span className="text-white font-rajdhani font-bold">{tournament.tier}</span>
                  </div>
                </div>

                {tournament.rounds && Object.keys(tournament.rounds).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <h4 className="text-white font-rajdhani font-bold text-lg mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-400" />
                      Tournament Rounds
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(tournament.rounds).map(([roundName, roundData], index) => {
                        const data = roundData as Record<string, any>;
                        return (
                          <div
                            key={roundName}
                            className="bg-gradient-to-br from-purple-500/15 to-blue-500/15 rounded-xl p-4 border border-purple-500/30"
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                <span className="text-white font-rajdhani font-bold text-sm">
                                  {index + 1}
                                </span>
                              </div>
                              <h5 className="text-white font-rajdhani font-bold text-lg">
                                {roundName}
                              </h5>
                            </div>
                            <div className="space-y-2 ml-10">
                              {data.matches && (
                                <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                                  <span className="text-gray-300 text-sm font-rajdhani font-semibold flex items-center gap-2">
                                    <Trophy className="w-4 h-4 text-yellow-500" />
                                    Total Matches
                                  </span>
                                  <span className="text-white font-rajdhani font-bold text-base">
                                    {data.matches}
                                  </span>
                                </div>
                              )}
                              {data.promotion && (
                                <div className="flex items-center justify-between bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg px-3 py-2 border border-yellow-500/30">
                                  <span className="text-gray-200 text-sm font-rajdhani font-semibold flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-yellow-400" />
                                    Promotion Rule
                                  </span>
                                  <span className="text-yellow-400 font-rajdhani font-bold text-base">
                                    {data.promotion}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {tournament.description && (
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
                  <h3 className="text-white font-rajdhani font-bold text-lg mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Description
                  </h3>
                  <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                    {tournament.description.split('\n').map((para, i) => (
                      <p key={i} className="mb-2">
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
                <h3 className="text-white font-rajdhani font-bold text-lg mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  Tournament Rules
                </h3>
                <ul className="space-y-3">
                  {[
                    '🎯 Fair Play Only — Play fairly and compete with your own skills.',
                    '🚫 No Hacking / Cheats — Hacks, scripts, injectors, mod menus, or any unfair tools are strictly prohibited.',
                    '📱 No Emulator — Playing through an emulator is strictly prohibited.',
                    '🤝 No Team-Up — Teaming up or collaborating with other players during the match is strictly prohibited.',
                    '👤 Own Account Only — Do not allow another person to play on your account.',
                    '⚡ No Unfair Advantage — Any method that gives you an unfair advantage over other players is prohibited.',
                    '📋 Follow Management Instructions — Players must follow all tournament and match instructions given by management.',
                    '📸 Proof May Be Required — Management may request screenshots, recordings, or other evidence when investigating a dispute or suspected violation.',
                    '⚠️ Disqualification — Any player found violating the rules may be immediately disqualified and may lose their rewards.',
                    '👑 Final Decision — Tournament Management has the final decision in all disputes, rule violations, and match-related matters.',
                  ].map((rule, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-gray-300 text-sm leading-relaxed"
                    >
                      <span className="text-purple-400 mt-0.5 shrink-0">▹</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {(tournament.ytLink || tournament.instaUser) && (
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
                  <h3 className="text-white font-rajdhani font-bold text-lg mb-3">
                    Connect With Us
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        if (tournament.ytLink && tournament.ytLink !== 'NOT UPDATED') {
                          window.open(tournament.ytLink, '_blank');
                        } else {
                          setShowYoutubeNotLiveModal(true);
                        }
                      }}
                      className={`flex items-center justify-center gap-2 py-3 rounded-xl font-rajdhani font-bold transition ${
                        tournament.ytLink && tournament.ytLink !== 'NOT UPDATED'
                          ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                          : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                      }`}
                    >
                      <Youtube className="w-5 h-5" />
                      {tournament.ytLink && tournament.ytLink !== 'NOT UPDATED' ? (
                        <span className="flex items-center gap-2">
                          Watch Live
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                        </span>
                      ) : (
                        'Not Live Yet'
                      )}
                    </motion.button>
                    {tournament.instaUser && (
                      <motion.a
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        href={`https://instagram.com/${tournament.instaUser}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-pink-500/20 py-3 rounded-xl text-pink-500 hover:bg-pink-500/30 transition font-rajdhani font-bold"
                      >
                        <Instagram className="w-5 h-5" /> Instagram
                      </motion.a>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'teams' && (
            <motion.div
              key="teams"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-3"
            >
              {joinedTeams.length === 0 ? (
                <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-12 text-center border border-purple-500/30">
                  <Users className="w-16 h-16 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 font-rajdhani text-lg">No teams joined yet</p>
                  <p className="text-gray-500 text-sm mt-1">Be the first to join the battle!</p>
                </div>
              ) : (
                joinedTeams.map((team, idx) => (
                  <motion.div
                    key={team.teamId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    whileHover={{ scale: 1.01 }}
                    onHoverStart={() => setHoveredTeam(team.teamId)}
                    onHoverEnd={() => setHoveredTeam(null)}
                    className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all"
                  >
                    <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 p-4">
                      <div className="flex justify-between items-center flex-wrap gap-2">
                        <div>
                          <h3 className="text-white font-rajdhani font-bold text-lg">
                            {team.teamName}
                          </h3>
                          <p className="text-gray-400 text-xs">ID: {team.teamId}</p>
                        </div>
                        <div className="flex gap-2">
                          <span className="px-2 py-1 bg-white/10 rounded-lg text-xs text-purple-400">
                            Slot {team.slotNo}
                          </span>
                          <span className="px-2 py-1 bg-white/10 rounded-lg text-xs text-blue-400">
                            Group {team.groupID}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Players ({team.players.length})
                      </p>
                      <div className="space-y-2">
                        {team.players.map((player, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-2 bg-white/5 rounded-xl"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                <span className="text-white font-rajdhani font-bold text-sm">
                                  {player.ign.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="text-white font-rajdhani font-bold text-sm">
                                  {player.ign}
                                </p>
                                <p className="text-gray-500 text-xs">ID: {player.name}</p>
                              </div>
                            </div>
                            {player.winning && player.winning > 0 && (
                              <div className="bg-green-500/20 px-2 py-1 rounded-lg">
                                <span className="text-green-400 text-xs font-bold">
                                  +{formatINR(player.winning)}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          )}

          {activeTab === 'prize' && (
            <motion.div
              key="prize"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30"
            >
              <h3 className="text-white font-rajdhani font-bold text-lg mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Prize Distribution
              </h3>
              <div className="space-y-3">
                {Object.keys(tournament.prizes).length > 0 ? (
                  Object.entries(tournament.prizes)
                    .sort(([, a], [, b]) => {
                      const va = parseInt(String(a).replace(/[₹,]/g, '')) || 0;
                      const vb = parseInt(String(b).replace(/[₹,]/g, '')) || 0;
                      return vb - va;
                    })
                    .map(([place, amount], idx) => {
                      const prizeAmount = String(amount).replace(/[₹,]/g, '');
                      if (idx === 0) {
                        return (
                          <div
                            key={idx}
                            className="relative overflow-hidden bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border-2 border-yellow-500/50"
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
                                  <Crown className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-yellow-500 font-rajdhani font-bold text-xl">
                                  {place}
                                </span>
                              </div>
                              <span className="text-3xl font-rajdhani font-bold text-yellow-500">
                                ₹{prizeAmount}
                              </span>
                            </div>
                          </div>
                        );
                      } else if (idx === 1) {
                        return (
                          <div
                            key={idx}
                            className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-400/20 to-gray-500/20 rounded-xl border border-gray-400/30"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-gray-400 to-gray-500 flex items-center justify-center">
                                <Medal className="w-5 h-5 text-white" />
                              </div>
                              <span className="text-gray-300 font-rajdhani font-bold text-lg">
                                {place}
                              </span>
                            </div>
                            <span className="text-2xl font-rajdhani font-bold text-gray-300">
                              ₹{prizeAmount}
                            </span>
                          </div>
                        );
                      } else if (idx === 2) {
                        return (
                          <div
                            key={idx}
                            className="flex justify-between items-center p-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-xl border border-orange-500/30"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                                <Medal className="w-5 h-5 text-white" />
                              </div>
                              <span className="text-orange-400 font-rajdhani font-bold text-lg">
                                {place}
                              </span>
                            </div>
                            <span className="text-2xl font-rajdhani font-bold text-orange-400">
                              ₹{prizeAmount}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={idx}
                          className="flex justify-between items-center p-3 bg-white/5 rounded-xl"
                        >
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-purple-400" />
                            <span className="text-white font-rajdhani font-bold">{place}</span>
                          </div>
                          <span className="text-purple-400 font-rajdhani font-bold text-lg">
                            ₹{prizeAmount}
                          </span>
                        </div>
                      );
                    })
                ) : (
                  <div className="text-center py-12">
                    <Gift className="w-16 h-16 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 font-rajdhani text-lg">
                      Prize distribution will be announced soon
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'result' && (
  <motion.div
    key="result"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="space-y-6"
  >
    {1 !== 1 ? (
      <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
        <div className="text-center py-12">
          <Timer className="w-16 h-16 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-rajdhani font-bold text-xl">
            Results Not Available Yet
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Results will be displayed after the tournament ends
          </p>
        </div>
      </div>
    ) : (
      <>
        {/* ==================== LEADERBOARD TABLE ==================== */}
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl border border-purple-500/30 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h3 className="text-white font-rajdhani font-bold text-lg">
              Leaderboard
            </h3>
          </div>

          {leaderboard.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No teams data available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-white/5">
                    <th className="px-3 py-3 text-left text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-3 py-3 text-left text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      Team Name
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      Wins
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      Matches
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      Kill.Pts
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      Fin.Pts
                    </th>
                    <th className="px-3 py-3 text-center text-[11px] font-rajdhani font-bold text-gray-400 uppercase tracking-wider">
                      Total.Pts
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((team, idx) => {
                    const isTop1 = idx === 0;
                    const isTop2 = idx === 1;
                    const isTop3 = idx === 2;
                    return (
                      <tr
                        key={team.teamId}
                        className={`border-t border-white/5 transition-colors hover:bg-white/[0.03] ${
                          isTop1
                            ? 'bg-yellow-500/10'
                            : isTop2
                            ? 'bg-gray-400/5'
                            : isTop3
                            ? 'bg-orange-500/5'
                            : ''
                        }`}
                      >
                        <td className="px-3 py-3">
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center font-rajdhani font-bold text-xs ${
                              isTop1
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                                : isTop2
                                ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                                : isTop3
                                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                                : 'bg-white/10 text-gray-300'
                            }`}
                          >
                            {idx + 1}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-white font-rajdhani font-bold text-sm">
                            {team.teamName}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center text-gray-300 font-rajdhani font-bold text-sm">
                          {team.wins}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-300 font-rajdhani font-bold text-sm">
                          {team.matchPlayed}
                        </td>
                        <td className="px-3 py-3 text-center text-blue-400 font-rajdhani font-bold text-sm">
                          {team.killPts}
                        </td>
                        <td className="px-3 py-3 text-center text-purple-400 font-rajdhani font-bold text-sm">
                          {team.finPts}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="inline-block px-2.5 py-1 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-lg text-white font-rajdhani font-bold text-sm">
                            {team.totalPts}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ==================== TOURNAMENT WINNERS ==================== */}
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 border border-purple-500/30">
          {!hasResults ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-3" />
              <p className="text-white font-rajdhani font-bold text-xl">Results Pending</p>
              <p className="text-gray-400 text-sm mt-2">
                Admin is verifying the results. Check back soon!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-white font-rajdhani font-bold text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Tournament Winners
              </h3>
              {joinedTeams
                .filter((t) => t.players.some((p) => p.winning && p.winning > 0))
                .map((team, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white/5 rounded-xl p-4"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-white font-rajdhani font-bold text-lg">
                        {team.teamName}
                      </span>
                      <span className="text-yellow-500 font-rajdhani font-bold text-xl">
                        {formatINR(
                          team.players.reduce((s, p) => s + (p.winning || 0), 0)
                        )}
                      </span>
                    </div>
                    {team.players.map((player, i) => (
                      <div key={i} className="flex justify-between text-sm py-1">
                        <span className="text-gray-400">{player.ign}</span>
                        {player.winning && player.winning > 0 && (
                          <span className="text-green-400 font-bold">
                            +{formatINR(player.winning)}
                          </span>
                        )}
                      </div>
                    ))}
                  </motion.div>
                ))}
            </div>
          )}
        </div>
      </>
    )}
  </motion.div>
)}
        </AnimatePresence>
      </div>

      {/* ============================================ */}
      {/* JOIN MODAL */}
      {/* ============================================ */}
      <AnimatePresence>
        {showJoinModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
            onClick={() => setShowJoinModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-md w-full max-h-[88vh] overflow-y-auto border border-purple-500/30 shadow-2xl"
            >
              <div className="sticky top-0 bg-gradient-to-r from-[#1a1a2e] to-[#0a0a0f] p-4 border-b border-purple-500/20 flex justify-between items-center z-10">
                <h2 className="text-white font-rajdhani font-bold text-xl">
                  {joinStep === 'slot'
                    ? 'Select Slot'
                    : isSolo
                    ? 'Join Tournament'
                    : `Join ${tournament.type} Tournament`}
                </h2>
                <button
                  onClick={() => setShowJoinModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4">
                <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-3 mb-4 border border-yellow-500/30">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Entry Fee</span>
                    <span className="text-yellow-500 font-rajdhani font-bold">
                      {formatINR(tournament.entryFee)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Your Balance</span>
                    <span className="text-white font-rajdhani font-bold">
                      {formatINR(userProfile?.balance || 0)}
                    </span>
                  </div>
                  {!hasSufficientTier() && (
                    <div className="mt-2 text-red-400 text-xs flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Requires {tournament.tier} tier. Your tier: {userProfile?.defaultTier}
                    </div>
                  )}
                </div>

                {/* FORM STEP */}
                {joinStep === 'form' && (
                  <>
                    {isSolo && (
                      <div className="space-y-4">
                        {isTournamentFull() ? (
                          <div className="bg-red-500/20 border-2 border-red-500 rounded-xl p-6 text-center">
                            <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
                            <h3 className="text-red-400 font-rajdhani font-bold text-xl">
                              Registration Closed
                            </h3>
                            <p className="text-gray-300 text-sm mt-2">
                              All {tournament.slots} solo slots are filled.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="bg-blue-500/10 rounded-xl p-3 border border-blue-500/30">
                              <User className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                              <p className="text-white font-rajdhani font-bold text-center">
                                Solo Tournament
                              </p>
                              <p className="text-gray-400 text-xs text-center mt-1">
                                You will be registered as an individual player
                              </p>
                            </div>

                            <div>
                              <label className="block text-gray-300 text-sm mb-1">
                                Your In-game Name (IGN) *
                              </label>
                              <input
                                type="text"
                                value={inGameName}
                                onChange={e => setInGameName(e.target.value)}
                                placeholder="Enter your in-game name"
                                className="w-full bg-white/5 border border-purple-500/30 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-purple-500 transition"
                              />
                            </div>

                            <div>
                              <label className="block text-gray-300 text-sm mb-1">
                                Your In-game ID *
                              </label>
                              <input
                                type="text"
                                value={inGameId}
                                onChange={e => setInGameId(e.target.value)}
                                placeholder="Enter your in-game ID"
                                className="w-full bg-white/5 border border-purple-500/30 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-purple-500 transition"
                              />
                            </div>

                            {(() => {
                              const reason = getSoloButtonDisabledReason();
                              const disabled = reason !== null;
                              return (
                                <>
                                  {disabled && (
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                                      <p className="text-yellow-400 text-xs flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {reason}
                                      </p>
                                    </div>
                                  )}
                                  <motion.button
                                    whileHover={disabled ? {} : { scale: 1.02 }}
                                    whileTap={disabled ? {} : { scale: 0.98 }}
                                    onClick={() => {
                                      setJoinMode('solo');
                                      setError('');
                                      if (!inGameName.trim() || !inGameId.trim()) {
                                        setError('Please enter your In-game Name and ID');
                                        return;
                                      }
                                      if ((userProfile?.balance || 0) < tournament.entryFee) {
                                        setError('Insufficient balance. Please add funds.');
                                        return;
                                      }
                                      setJoinStep('slot');
                                    }}
                                    disabled={disabled}
                                    className="w-full bg-gradient-to-r from-purple-500 to-blue-500 py-3 rounded-xl text-white font-rajdhani font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Continue to Select Slot
                                  </motion.button>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    )}


                      <>
                        {/* Join options */}
{(isSquad || tournament.multiOption) && (
  <div className="bg-white/5 rounded-xl p-1 mb-4">
    <div className="flex gap-1">

      {/* My Team - Squad only */}
      {isSquad && (
        <button
          onClick={() => setJoinMode('verified')}
          className={`flex-1 py-2 rounded-lg font-rajdhani font-bold text-sm transition-all ${
            joinMode === 'verified'
              ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-1" />
          My Team
        </button>
      )}

      {/* Join Solo - only when joinType is true */}
      {tournament.multiOption && (
        <button
          onClick={() => {setJoinMode('solo')
            console.log(`hii:${tournament?.multiOption}`)
                  console.log('hi')}
          }
          className={`flex-1 py-2 rounded-lg font-rajdhani font-bold text-sm transition-all ${
            joinMode === 'solo'
              ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <User className="w-4 h-4 inline mr-1" />
          Join Solo
        </button>
      )}

    </div>
  </div>
)}
                        {isSquad && joinMode === 'verified' && (
                          <div className="space-y-3">
                            {loadingVerifiedTeam ? (
                              <div className="bg-white/5 rounded-xl p-6 text-center">
                                <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
                                <p className="text-gray-400 text-sm">Loading your team...</p>
                              </div>
                            ) : userVerifiedTeam ? (
                              <div
                                className={`rounded-xl p-4 border ${
                                  canVerifiedTeamJoin()
                                    ? 'bg-green-500/10 border-green-500/30'
                                    : 'bg-red-500/10 border-red-500/30'
                                }`}
                              >
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <p className="text-white font-rajdhani font-bold text-lg">
                                      {userVerifiedTeam.teamName}
                                    </p>
                                    <p className="text-gray-400 text-xs">
                                      ID: {userVerifiedTeam.teamId}
                                    </p>
                                  </div>
                                  <div
                                    className={`px-2 py-1 rounded-full text-xs ${
                                      userVerifiedTeam.status === 'complete'
                                        ? 'bg-green-500/20 text-green-400'
                                        : 'bg-yellow-500/20 text-yellow-400'
                                    }`}
                                  >
                                    {userVerifiedTeam.status === 'complete'
                                      ? 'Verified'
                                      : 'Incomplete'}
                                  </div>
                                </div>

                                <div className="space-y-2 mb-4">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Players:</span>
                                    <span className="text-white font-rajdhani font-bold">
                                      {Object.keys(userVerifiedTeam.players || {}).length}/
                                      {playersPerTeam}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Entry Fee Per Player:</span>
                                    <span className="text-yellow-500 font-rajdhani font-bold">
                                      {formatINR(tournament.entryFee)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Total Fee:</span>
                                    <span className="text-yellow-500 font-rajdhani font-bold">
                                      {formatINR(
                                        tournament.entryFee *
                                          Object.keys(userVerifiedTeam.players || {}).length
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm border-t border-white/10 pt-2">
                                    <span className="text-gray-400">Your Balance:</span>
                                    <span
                                      className={`font-rajdhani font-bold ${
                                        (userProfile?.balance || 0) >=
                                        tournament.entryFee *
                                          Object.keys(userVerifiedTeam.players || {}).length
                                          ? 'text-green-400'
                                          : 'text-red-400'
                                      }`}
                                    >
                                      {formatINR(userProfile?.balance || 0)}
                                    </span>
                                  </div>
                                </div>

                                {canVerifiedTeamJoin() ? (
                                  (() => {
                                    const reason = getVerifiedButtonDisabledReason();
                                    const disabled = reason !== null;
                                    return (
                                      <>
                                        {disabled && (
                                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 mb-2">
                                            <p className="text-yellow-400 text-xs flex items-center gap-1">
                                              <AlertCircle className="w-3 h-3" />
                                              {reason}
                                            </p>
                                          </div>
                                        )}
                                        <motion.button
                                          whileHover={disabled ? {} : { scale: 1.02 }}
                                          whileTap={disabled ? {} : { scale: 0.98 }}
                                          onClick={() => handleContinueToSlot('verified')}
                                          disabled={disabled}
                                          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 py-3 rounded-xl text-white font-rajdhani font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          Pay ₹
                                          {tournament.entryFee *
                                            Object.keys(userVerifiedTeam.players || {}).length}{' '}
                                          & Select Slot
                                        </motion.button>
                                      </>
                                    );
                                  })()
                                ) : (
                                  <div>
                                    <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-3">
                                      <p className="text-red-400 text-sm flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" />
                                        Your team needs {getRequiredPlayersText()}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => navigate('/dashboard/my-team')}
                                      className="w-full bg-purple-500/20 py-3 rounded-xl text-purple-400 font-rajdhani font-bold"
                                    >
                                      Verify Team Now →
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="bg-yellow-500/10 rounded-xl p-6 text-center border border-yellow-500/30">
                                <Building2 className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                                <p className="text-yellow-400 text-lg font-rajdhani font-bold mb-2">
                                  No Team Found
                                </p>
                                <p className="text-gray-400 text-sm mb-4">
                                  You don't have a Completed team yet
                                </p>
                                <button
                                  onClick={() => navigate('/dashboard/my-team')}
                                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white font-rajdhani font-bold"
                                >
                                  Create a Team →
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {joinMode === 'solo' && tournament.multiOption && (
                          <div className="space-y-3">
                            <div className="bg-blue-500/10 rounded-xl p-3 border border-blue-500/30">
                              <p className="text-blue-400 text-xs flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                You'll pick a slot next. If the slot is empty, a temp team will be
                                created for you. If partially filled, you'll join it.
                              </p>
                            </div>

                            <div>
                              <label className="block text-gray-300 text-sm mb-1">
                                Your In-game Name (IGN) *
                              </label>
                              <input
                                type="text"
                                value={inGameName}
                                onChange={e => setInGameName(e.target.value)}
                                placeholder="Your in-game name"
                                className="w-full bg-white/5 border border-purple-500/30 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-purple-500 transition"
                              />
                            </div>

                            <div>
                              <label className="block text-gray-300 text-sm mb-1">
                                Your In-game ID *
                              </label>
                              <input
                                type="text"
                                value={inGameId}
                                onChange={e => setInGameId(e.target.value)}
                                placeholder="Your in-game ID"
                                className="w-full bg-white/5 border border-purple-500/30 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-purple-500 transition"
                              />
                            </div>

                            {(() => {
                              const reason = getSoloButtonDisabledReason();
                              const disabled = reason !== null;
                              return (
                                <>
                                  {disabled && (
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                                      <p className="text-yellow-400 text-xs flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {reason}
                                      </p>
                                    </div>
                                  )}
                                  <motion.button
                                    whileHover={disabled ? {} : { scale: 1.02 }}
                                    whileTap={disabled ? {} : { scale: 0.98 }}
                                    onClick={() => handleContinueToSlot('solo')}
                                    disabled={disabled}
                                    className="w-full bg-gradient-to-r from-purple-500 to-blue-500 py-3 rounded-xl text-white font-rajdhani font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Continue to Select Slot - {formatINR(tournament.entryFee)}
                                  </motion.button>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </>

                  </>
                )}

                {joinStep === 'slot' && renderSlotGrid()}

                {error && (
                  <div className="mt-3 bg-red-500/20 border border-red-500 rounded-xl p-3">
                    <p className="text-red-400 text-xs text-center flex items-center justify-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {error}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TEAM DETAILS MODAL */}
      <AnimatePresence>
        {showTeamDetailsModal && userTeam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
            onClick={() => setShowTeamDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-md w-full border border-purple-500/30 shadow-2xl"
            >
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 p-4 border-b border-purple-500/20 flex justify-between items-center">
                <h3 className="text-white font-rajdhani font-bold text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  My Team
                </h3>
                <button
                  onClick={() => setShowTeamDetailsModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-gray-400 text-xs mb-1">Team Name</p>
                  <p className="text-white font-rajdhani font-bold text-lg">{userTeam.teamName}</p>
                </div>

                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-gray-400 text-xs mb-1">Team ID</p>
                  <div className="flex items-center justify-between">
                    <code className="text-white font-mono">{userTeam.teamId}</code>
                    <button
                      onClick={() => copyToClipboard(userTeam.teamId)}
                      className="flex items-center gap-1 text-purple-400 text-sm"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-gray-400 text-xs mb-1">Team Password</p>
                  <code className="text-white font-mono">
                    {userTeam.password || 'No password'}
                  </code>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-gray-400 text-xs">Slot No.</p>
                    <p className="text-white font-rajdhani font-bold text-2xl">{userTeam.slotNo}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-gray-400 text-xs">Group ID</p>
                    <p className="text-white font-rajdhani font-bold text-2xl">
                      {userTeam.groupID}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-gray-400 text-sm mb-2">Team Members</p>
                  <div className="space-y-2">
                    {userTeam.players.map((player, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-2 bg-white/5 rounded-xl"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                            <span className="text-white font-rajdhani font-bold text-sm">
                              {player.ign.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-rajdhani font-bold text-sm">
                              {player.ign}
                            </p>
                            <p className="text-gray-500 text-xs">ID: {player.name}</p>
                          </div>
                        </div>
                        {player.uid === auth.currentUser?.uid && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-lg">
                            You
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-purple-500/20">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowTeamDetailsModal(false)}
                  className="w-full bg-gradient-to-r from-purple-500 to-blue-500 py-2 rounded-xl text-white font-rajdhani font-bold"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* YOUTUBE NOT LIVE MODAL */}
      <AnimatePresence>
        {showYoutubeNotLiveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
            onClick={() => setShowYoutubeNotLiveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-md w-full p-6 border border-yellow-500/30 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                <Youtube className="w-10 h-10 text-yellow-500" />
              </div>
              <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">Not Live Yet</h2>
              <p className="text-gray-400 text-sm mb-4">
                This tournament hasn't started yet. The live stream will be available once the
                tournament begins.
              </p>
              <div className="bg-yellow-500/10 rounded-xl p-3 border border-yellow-500/20 mb-4">
                <p className="text-yellow-400 text-xs flex items-center justify-center gap-2">
                  <Timer className="w-4 h-4" />
                  Check back at match time to watch live!
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowYoutubeNotLiveModal(false)}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-500 py-3 rounded-xl text-white font-rajdhani font-bold"
              >
                Got it
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
        .font-rajdhani { font-family: 'Rajdhani', sans-serif; }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
        .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
        .animate-spin-slow { animation: spin 8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TournamentDetails;